import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft,
  Search,
  HeartPulse,
  Shield,
  ChevronRight,
  Building2,
  Check,
  X,
  Plus,
  Minus,
  Info,
  Filter,
  LayoutGrid,
  List,
  Activity
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Company logos - Official sources (Wikimedia Commons / Official websites)
const COMPANY_LOGOS = {
  // Health Insurance Companies
  "hdfc-ergo": "https://upload.wikimedia.org/wikipedia/commons/a/aa/HDFC_ERGO_General_Insurance_Company.svg",
  "care-health": "https://upload.wikimedia.org/wikipedia/commons/9/90/Care_health_insurance_logo.png",
  "icici-lombard": "https://upload.wikimedia.org/wikipedia/en/thumb/0/05/ICICI_Lombard.svg/250px-ICICI_Lombard.svg.png",
  "aditya-birla": "https://upload.wikimedia.org/wikipedia/en/7/75/Aditya_Birla_Group_Logo.svg",
  "manipal-cigna": "https://upload.wikimedia.org/wikipedia/en/c/c1/ManipalCigna_Logo.png",
  "niva-bupa": "https://upload.wikimedia.org/wikipedia/commons/f/fb/Niva_Bupa_Logo.svg",
  "tata-aig": "https://upload.wikimedia.org/wikipedia/commons/8/8e/Tata_AIG_General_Insurance_Logo.svg",
  "star-health": "https://upload.wikimedia.org/wikipedia/en/7/7e/Star_Health_Insurance_logo.png",
  // Life Insurance Companies (Term) - Verified working URLs
  "hdfc": "https://upload.wikimedia.org/wikipedia/commons/b/b5/HDFC-Life-Logo.jpg",
  "hdfc-life": "https://upload.wikimedia.org/wikipedia/commons/b/b5/HDFC-Life-Logo.jpg",
  "tata": "https://upload.wikimedia.org/wikipedia/commons/4/4f/TataAIA.png",
  "tata-aia": "https://upload.wikimedia.org/wikipedia/commons/4/4f/TataAIA.png",
  "tata-aia-life": "https://upload.wikimedia.org/wikipedia/commons/4/4f/TataAIA.png",
  "icici": "https://upload.wikimedia.org/wikipedia/commons/9/91/ICICI-Prudential-Mutual-Fund.jpg",
  "icici-prudential": "https://upload.wikimedia.org/wikipedia/commons/9/91/ICICI-Prudential-Mutual-Fund.jpg",
  "icici-prudential-life": "https://upload.wikimedia.org/wikipedia/commons/9/91/ICICI-Prudential-Mutual-Fund.jpg",
  "bajaj": "https://upload.wikimedia.org/wikipedia/commons/thumb/8/81/Bajaj_Allianz_Insurance.svg/200px-Bajaj_Allianz_Insurance.svg.png",
  "bajaj-allianz": "https://upload.wikimedia.org/wikipedia/commons/thumb/8/81/Bajaj_Allianz_Insurance.svg/200px-Bajaj_Allianz_Insurance.svg.png",
  "bajaj-allianz-life": "https://upload.wikimedia.org/wikipedia/commons/thumb/8/81/Bajaj_Allianz_Insurance.svg/200px-Bajaj_Allianz_Insurance.svg.png",
  "axis": "https://upload.wikimedia.org/wikipedia/commons/5/58/Axis_Max_Life_Insurance_logo.svg",
  "max": "https://upload.wikimedia.org/wikipedia/commons/5/58/Axis_Max_Life_Insurance_logo.svg",
  "axis-max-life": "https://upload.wikimedia.org/wikipedia/commons/5/58/Axis_Max_Life_Insurance_logo.svg",
  "max-life": "https://upload.wikimedia.org/wikipedia/commons/0/04/Max_Life_Insurance_logo.svg"
};

// Term Plan Key Features to show
const TERM_CHECKLIST_FEATURES = [
  { id: "rop", label: "Return of Premium", description: "Get premiums back on survival" },
  { id: "ti-benefit", label: "Terminal Illness", description: "Payout on TI diagnosis" },
  { id: "ci-cover", label: "Critical Illness", description: "CI coverage available" },
  { id: "joint-life", label: "Joint Life", description: "Covers both spouses" },
  { id: "women-benefit", label: "Women Benefits", description: "Special benefits for women" },
  { id: "instant-claim", label: "Instant Claim", description: "Same-day claim payout" }
];

// Extract term plan feature status
const extractTermFeatureStatus = (plan) => {
  const features = {};
  
  // Return of Premium
  const hasROP = plan.variants?.some(v => 
    v.name?.toLowerCase().includes("rop") || 
    v.name?.toLowerCase().includes("return of premium") ||
    v.description?.toLowerCase().includes("return of premium")
  ) || plan.why_choose?.some(w => w.toLowerCase().includes("premium") && w.toLowerCase().includes("return"));
  
  features["rop"] = hasROP 
    ? { status: FeatureStatus.INCLUDED, note: "Available" }
    : { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };
  
  // Terminal Illness Benefit
  const hasTI = plan.core_benefits?.some(b => 
    b.name?.toLowerCase().includes("terminal illness") || 
    b.description?.toLowerCase().includes("terminal illness")
  ) || plan.death_benefit?.terminal_illness;
  
  features["ti-benefit"] = hasTI
    ? { status: FeatureStatus.INCLUDED, note: "Included" }
    : { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };
  
  // Critical Illness Cover
  const hasCIRider = plan.core_benefits?.some(b => 
    b.name?.toLowerCase().includes("critical illness")
  ) || plan.riders_available?.some(r => r.toLowerCase().includes("critical illness"));
  
  features["ci-cover"] = hasCIRider
    ? { status: FeatureStatus.ADDON, note: "Rider available" }
    : { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };
  
  // Joint Life
  const hasJointLife = plan.variants?.some(v => 
    v.name?.toLowerCase().includes("joint")
  ) || plan.policy_options?.types?.includes("Joint Life (with Spouse)");
  
  features["joint-life"] = hasJointLife
    ? { status: FeatureStatus.INCLUDED, note: "Available" }
    : { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };
  
  // Women Benefits
  const hasWomenBenefits = plan.core_benefits?.some(b => 
    b.name?.toLowerCase().includes("women")
  ) || plan.additional_benefits?.some(b => 
    b.name?.toLowerCase().includes("women") || b.name?.toLowerCase().includes("female")
  );
  
  features["women-benefit"] = hasWomenBenefits
    ? { status: FeatureStatus.INCLUDED, note: "15% discount" }
    : { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };
  
  // Instant Claim
  const hasInstantClaim = plan.core_benefits?.some(b => 
    b.name?.toLowerCase().includes("instant") || 
    b.name?.toLowerCase().includes("accelerated")
  );
  
  features["instant-claim"] = hasInstantClaim
    ? { status: FeatureStatus.INCLUDED, note: "Within 1 day" }
    : { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };
  
  return features;
};

// Personal Accident Plan Key Features to show
const PA_CHECKLIST_FEATURES = [
  { id: "accidental-death", label: "Accidental Death", description: "100% lump sum payout to family" },
  { id: "permanent-disability", label: "Permanent Disability", description: "PTD & PPD lump sum payouts" },
  { id: "weekly-income", label: "Weekly Income (TTD)", description: "Salary-like payout while you recover" },
  { id: "bones-burns", label: "Broken Bones & Burns", description: "Fixed payout for fractures & burns" },
  { id: "child-education", label: "Child Education", description: "Extra payout for children's education" },
  { id: "worldwide", label: "Worldwide Cover", description: "24x7 cover anywhere in the world" }
];

// Extract PA plan feature status
const extractPAFeatureStatus = (plan) => {
  const features = {};
  const coreIds = plan.core_covers?.map(c => c.id) || [];

  features["accidental-death"] = coreIds.includes("accidental-death")
    ? { status: FeatureStatus.INCLUDED, note: "100% of SI" }
    : { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };

  features["permanent-disability"] = (coreIds.includes("ptd") || coreIds.includes("ppd"))
    ? { status: FeatureStatus.INCLUDED, note: "Up to 100%" }
    : { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };

  features["weekly-income"] = (() => {
    const ttdCover = plan.core_covers?.find(c => c.id === "ttd");
    if (!ttdCover) return { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };
    return ttdCover.optional
      ? { status: FeatureStatus.ADDON, note: "Optional cover" }
      : { status: FeatureStatus.INCLUDED, note: "Included" };
  })();

  const hasBones = !!plan.broken_bones_table 
    || plan.optional_covers?.some(o => o.name?.toLowerCase().includes("bones"))
    || plan.additional_benefits?.some(b => /fracture|bones|burns/.test(b.name?.toLowerCase() || ""));
  const bonesIsBase = plan.additional_benefits?.some(b => /fracture|bones/.test(b.name?.toLowerCase() || ""));
  features["bones-burns"] = hasBones
    ? (bonesIsBase ? { status: FeatureStatus.INCLUDED, note: "Built-in" } : { status: FeatureStatus.ADDON, note: "Optional cover" })
    : { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };

  const hasEdu = plan.additional_benefits?.some(b => b.name?.toLowerCase().includes("education"));
  features["child-education"] = hasEdu
    ? { status: FeatureStatus.INCLUDED, note: "Included" }
    : { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };

  features["worldwide"] = plan.worldwide_cover
    ? { status: FeatureStatus.INCLUDED, note: "24x7 global" }
    : { status: FeatureStatus.NOT_AVAILABLE, note: "India only" };

  return features;
};

// Key checklist features to evaluate
const HEALTH_CHECKLIST_FEATURES = [
  { id: "any-room", label: "Any Room", description: "No room rent restriction" },
  { id: "no-copay", label: "No Co-pay", description: "100% claim settlement" },
  { id: "restoration", label: "Restoration", description: "SI refills after claim" },
  { id: "day1-ped", label: "Day 1 PED Cover", description: "Pre-existing covered from day 1" },
  { id: "ncb", label: "No Claim Bonus", description: "Bonus for claim-free years" },
  { id: "maternity", label: "Maternity", description: "Pregnancy coverage" }
];

// Feature status types
const FeatureStatus = {
  INCLUDED: "included",    // ✓ Green - Included in base plan
  ADDON: "addon",          // + Amber - Available as add-on
  LIMITED: "limited",      // ~ Blue - Available with conditions
  NOT_AVAILABLE: "not"     // ✗ Gray - Not available
};

// Extract feature status from plan data
const extractFeatureStatus = (plan) => {
  const features = {};
  
  // Any Room - Check room rent in coverage details
  const roomRent = plan.coverage_details?.hospitalization?.room_rent || "";
  const roomRentLower = roomRent.toLowerCase();
  
  if (roomRentLower.includes("actual") || roomRentLower.includes("no limit") || roomRentLower.includes("no sub-limit") || roomRentLower.includes("any room")) {
    features["any-room"] = { status: FeatureStatus.INCLUDED, note: "Any room" };
  } else if (roomRentLower.includes("single private") || roomRentLower.includes("single ac")) {
    features["any-room"] = { status: FeatureStatus.LIMITED, note: "Single Private AC" };
  } else if (plan.riders?.some(r => r.name?.toLowerCase().includes("room") || r.name?.toLowerCase().includes("any"))) {
    features["any-room"] = { status: FeatureStatus.ADDON, note: "Add-on available" };
  } else {
    features["any-room"] = { status: FeatureStatus.LIMITED, note: roomRent.split('(')[0]?.trim() || "Limited" };
  }
  
  // No Co-pay - Check copay in coverage
  const hasCopay = plan.coverage_details?.hospitalization?.co_payment || 
                   plan.usp?.some(u => u.toLowerCase().includes("co-pay")) ||
                   plan.name?.toLowerCase().includes("copay");
  
  if (plan.usp?.some(u => u.toLowerCase().includes("no co-pay") || u.toLowerCase().includes("zero copay"))) {
    features["no-copay"] = { status: FeatureStatus.INCLUDED, note: "No co-pay" };
  } else if (plan.variants?.some(v => v.name?.toLowerCase().includes("no copay"))) {
    features["no-copay"] = { status: FeatureStatus.LIMITED, note: "In select variants" };
  } else {
    features["no-copay"] = { status: FeatureStatus.INCLUDED, note: "No mandatory co-pay" };
  }
  
  // Restoration Benefit
  const hasRestore = plan.core_benefits?.some(b => 
    b.name?.toLowerCase().includes("restore") || 
    b.name?.toLowerCase().includes("reload") ||
    b.name?.toLowerCase().includes("recharge")
  ) || plan.usp?.some(u => 
    u.toLowerCase().includes("restore") || 
    u.toLowerCase().includes("reload")
  );
  
  const restoreAsRider = plan.riders?.some(r => 
    r.name?.toLowerCase().includes("restore") || 
    r.name?.toLowerCase().includes("reload")
  );
  
  if (hasRestore) {
    features["restoration"] = { status: FeatureStatus.INCLUDED, note: "100% restore" };
  } else if (restoreAsRider) {
    features["restoration"] = { status: FeatureStatus.ADDON, note: "Add-on available" };
  } else {
    features["restoration"] = { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };
  }
  
  // Day 1 PED Cover
  const day1PED = plan.riders?.some(r => 
    r.name?.toLowerCase().includes("day 1") || 
    r.name?.toLowerCase().includes("day one") ||
    r.name?.toLowerCase().includes("ped") && r.name?.toLowerCase().includes("waiver")
  ) || plan.usp?.some(u => 
    u.toLowerCase().includes("day 1 ped") || 
    u.toLowerCase().includes("zero waiting")
  );
  
  const pedWaiting = plan.waiting_periods?.pre_existing?.period || "48 months";
  const pedMonths = parseInt(pedWaiting) || 48;
  
  if (plan.usp?.some(u => u.toLowerCase().includes("day 1 ped cover"))) {
    features["day1-ped"] = { status: FeatureStatus.ADDON, note: "Add-on available" };
  } else if (day1PED) {
    features["day1-ped"] = { status: FeatureStatus.ADDON, note: "Add-on available" };
  } else if (pedMonths <= 24) {
    features["day1-ped"] = { status: FeatureStatus.LIMITED, note: `${pedMonths/12}yr wait` };
  } else {
    features["day1-ped"] = { status: FeatureStatus.LIMITED, note: `${pedMonths/12}yr wait` };
  }
  
  // No Claim Bonus
  const hasNCB = plan.core_benefits?.some(b => 
    b.name?.toLowerCase().includes("claim bonus") || 
    b.name?.toLowerCase().includes("ncb") ||
    b.name?.toLowerCase().includes("cumulative bonus")
  ) || plan.usp?.some(u => 
    u.toLowerCase().includes("claim bonus") || 
    u.toLowerCase().includes("ncb")
  );
  
  if (hasNCB) {
    features["ncb"] = { status: FeatureStatus.INCLUDED, note: "Up to 100%" };
  } else {
    features["ncb"] = { status: FeatureStatus.INCLUDED, note: "Available" };
  }
  
  // Maternity Coverage
  const hasMaternity = plan.coverage_details?.hospitalization?.maternity || 
                       plan.core_benefits?.some(b => b.name?.toLowerCase().includes("maternity")) ||
                       plan.usp?.some(u => u.toLowerCase().includes("maternity"));
  
  const maternityRider = plan.riders?.some(r => r.name?.toLowerCase().includes("maternity"));
  
  if (hasMaternity) {
    features["maternity"] = { status: FeatureStatus.INCLUDED, note: "Included" };
  } else if (maternityRider) {
    features["maternity"] = { status: FeatureStatus.ADDON, note: "Add-on available" };
  } else {
    features["maternity"] = { status: FeatureStatus.NOT_AVAILABLE, note: "Not available" };
  }
  
  return features;
};

// Feature indicator component
const FeatureIndicator = ({ status, note, compact = false }) => {
  const configs = {
    [FeatureStatus.INCLUDED]: {
      icon: Check,
      bgColor: "bg-green-500/20",
      iconColor: "text-green-600",
      borderColor: "border-green-500/30"
    },
    [FeatureStatus.ADDON]: {
      icon: Plus,
      bgColor: "bg-[#D4A017]/15",
      iconColor: "text-[#A87608]",
      borderColor: "border-[#D4A017]/30"
    },
    [FeatureStatus.LIMITED]: {
      icon: Minus,
      bgColor: "bg-[#EDF1F8]/40",
      iconColor: "text-[#2E3B57]",
      borderColor: "border-blue-500/30"
    },
    [FeatureStatus.NOT_AVAILABLE]: {
      icon: X,
      bgColor: "bg-[#EDF1F8]/20",
      iconColor: "text-[#3A4557]",
      borderColor: "border-[#C9D4E6]/30"
    }
  };
  
  const config = configs[status] || configs[FeatureStatus.NOT_AVAILABLE];
  const Icon = config.icon;
  
  if (compact) {
    return (
      <div className={`h-6 w-6 rounded-full ${config.bgColor} border ${config.borderColor} flex items-center justify-center`}>
        <Icon className={`h-3.5 w-3.5 ${config.iconColor}`} />
      </div>
    );
  }
  
  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md ${config.bgColor} border ${config.borderColor}`}>
      <Icon className={`h-3.5 w-3.5 ${config.iconColor}`} />
      <span className={`text-xs ${config.iconColor}`}>{note}</span>
    </div>
  );
};

// Legend component
const Legend = () => (
  <div className="flex flex-wrap items-center gap-4 text-xs">
    <div className="flex items-center gap-1.5">
      <div className="h-5 w-5 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center">
        <Check className="h-3 w-3 text-green-600" />
      </div>
      <span className="text-[#3A4557]">Included</span>
    </div>
    <div className="flex items-center gap-1.5">
      <div className="h-5 w-5 rounded-full bg-[#D4A017]/15 border border-[#D4A017]/30 flex items-center justify-center">
        <Plus className="h-3 w-3 text-[#A87608]" />
      </div>
      <span className="text-[#3A4557]">Add-on</span>
    </div>
    <div className="flex items-center gap-1.5">
      <div className="h-5 w-5 rounded-full bg-[#EDF1F8]/40 border border-blue-500/30 flex items-center justify-center">
        <Minus className="h-3 w-3 text-[#2E3B57]" />
      </div>
      <span className="text-[#3A4557]">Conditional</span>
    </div>
    <div className="flex items-center gap-1.5">
      <div className="h-5 w-5 rounded-full bg-[#EDF1F8]/20 border border-[#C9D4E6]/30 flex items-center justify-center">
        <X className="h-3 w-3 text-[#3A4557]" />
      </div>
      <span className="text-[#3A4557]">Not Available</span>
    </div>
  </div>
);

// Plan Card Component
const PlanCard = ({ plan, onClick }) => {
  const features = extractFeatureStatus(plan);
  const termFeatures = extractTermFeatureStatus(plan);
  const paFeatures = extractPAFeatureStatus(plan);
  const isTermPlan = plan.type === "term";
  const isPAPlan = plan.type === "pa";
  
  // Count positive features
  const positiveCount = isPAPlan
    ? Object.values(paFeatures).filter(f => f.status === FeatureStatus.INCLUDED || f.status === FeatureStatus.ADDON).length
    : isTermPlan 
    ? Object.values(termFeatures).filter(f => f.status === FeatureStatus.INCLUDED || f.status === FeatureStatus.ADDON).length
    : Object.values(features).filter(f => f.status === FeatureStatus.INCLUDED || f.status === FeatureStatus.ADDON).length;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onClick={onClick}
      className={`glass-card p-5 cursor-pointer group relative overflow-hidden ${
        isPAPlan
          ? "border-amber-500/20 hover:border-amber-500/50"
          : isTermPlan 
          ? "border-[#C9D4E6] hover:border-blue-500/50" 
          : "border-emerald-500/20 hover:border-emerald-500/50"
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      {/* Type Badge */}
      <div className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-medium ${
        isPAPlan ? "bg-amber-500/20 text-amber-600" : isTermPlan ? "bg-[#EDF1F8]/40 text-[#2E3B57]" : "bg-emerald-500/20 text-emerald-600"
      }`}>
        {isPAPlan ? "Accident" : isTermPlan ? "Term" : "Health"}
      </div>

      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="h-12 w-12 rounded-xl bg-white p-1.5 flex items-center justify-center flex-shrink-0 overflow-hidden">
          <img 
            src={plan.company_logo || COMPANY_LOGOS[plan.id.split('-')[0]] || COMPANY_LOGOS[plan.company_id] || ""}
            alt={plan.company}
            className="max-h-8 max-w-full object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden items-center justify-center w-full h-full text-[#0D1A3A] font-bold text-sm">
            {plan.company_short || plan.company?.split(' ').slice(0, 2).map(w => w[0]).join('')}
          </div>
        </div>
        <div className="flex-1 min-w-0 pr-12">
          <p className="text-xs text-[#3A4557] mb-0.5">{plan.company_short || plan.company}</p>
          <h3 className="font-bold text-[#1B2A4A] text-base leading-tight line-clamp-2 group-hover:text-emerald-600 transition-colors">
            {plan.name}
          </h3>
        </div>
      </div>

      {/* Sum Insured */}
      <div className="mb-4 p-2.5 rounded-lg bg-[#EDF1F8]/50">
        <div className="flex justify-between items-center">
          <span className="text-xs text-[#3A4557]">{isPAPlan ? "Cover Amount" : isTermPlan ? "Coverage" : "Sum Insured"}</span>
          <span className="text-[#1B2A4A] font-medium text-sm">
            {plan.sum_insured_options?.[0]} - {plan.sum_insured_options?.[plan.sum_insured_options.length - 1]}
          </span>
        </div>
      </div>

      {/* Feature Checklist - PA Plans */}
      {isPAPlan && (
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-xs text-[#3A4557] mb-2">
            <span>Key Features</span>
            <span className="text-amber-600">{positiveCount}/6 covered</span>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            {PA_CHECKLIST_FEATURES.map(feature => {
              const status = paFeatures[feature.id] || { status: FeatureStatus.NOT_AVAILABLE, note: "" };
              return (
                <div key={feature.id} className="flex items-center gap-2">
                  <FeatureIndicator status={status.status} compact />
                  <span className={`text-xs ${
                    status.status === FeatureStatus.INCLUDED ? "text-[#2E3B57]" :
                    status.status === FeatureStatus.ADDON ? "text-[#A87608]/80" :
                    status.status === FeatureStatus.LIMITED ? "text-[#2E3B57]/80" :
                    "text-[#3A4557]"
                  }`}>
                    {feature.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Feature Checklist - Term Plans */}
      {isTermPlan && (
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-xs text-[#3A4557] mb-2">
            <span>Key Features</span>
            <span className="text-[#2E3B57]">{positiveCount}/6 covered</span>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            {TERM_CHECKLIST_FEATURES.map(feature => {
              const status = termFeatures[feature.id] || { status: FeatureStatus.NOT_AVAILABLE, note: "" };
              return (
                <div key={feature.id} className="flex items-center gap-2">
                  <FeatureIndicator status={status.status} compact />
                  <span className={`text-xs ${
                    status.status === FeatureStatus.INCLUDED ? "text-[#2E3B57]" :
                    status.status === FeatureStatus.ADDON ? "text-[#A87608]/80" :
                    status.status === FeatureStatus.LIMITED ? "text-[#2E3B57]/80" :
                    "text-[#3A4557]"
                  }`}>
                    {feature.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Feature Checklist - Health Plans */}
      {!isTermPlan && !isPAPlan && (
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-xs text-[#3A4557] mb-2">
            <span>Key Features</span>
            <span className="text-emerald-600">{positiveCount}/6 covered</span>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            {HEALTH_CHECKLIST_FEATURES.map(feature => {
              const status = features[feature.id] || { status: FeatureStatus.NOT_AVAILABLE, note: "" };
              return (
                <div key={feature.id} className="flex items-center gap-2">
                  <FeatureIndicator status={status.status} compact />
                  <span className={`text-xs ${
                    status.status === FeatureStatus.INCLUDED ? "text-[#2E3B57]" :
                    status.status === FeatureStatus.ADDON ? "text-[#A87608]/80" :
                    status.status === FeatureStatus.LIMITED ? "text-[#2E3B57]/80" :
                    "text-[#3A4557]"
                  }`}>
                    {feature.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Key Highlight */}
      {(plan.usp?.[0] || plan.why_choose?.[0]) && (
        <div className="text-xs text-[#3A4557] mb-4 line-clamp-2">
          <span className={isPAPlan ? "text-amber-600" : isTermPlan ? "text-[#2E3B57]" : "text-emerald-600"}>★</span> {plan.usp?.[0] || plan.why_choose?.[0]}
        </div>
      )}

      {/* Claim Settlement Ratio for Term Plans */}
      {isTermPlan && plan.claim_settlement_ratio && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-xs text-[#3A4557]">Claim Settlement:</span>
          <span className="text-xs font-medium text-green-600">{plan.claim_settlement_ratio}%</span>
        </div>
      )}

      {/* CTA */}
      <div className={`flex items-center text-sm font-medium ${
        isTermPlan ? "text-[#2E3B57]" : "text-[#A87608]"
      } group-hover:translate-x-1 transition-transform`}>
        View Details
        <ChevronRight className="h-4 w-4 ml-1" />
      </div>
    </motion.div>
  );
};

export default function ExplorePlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, companiesRes] = await Promise.all([
        axios.get(`${API}/plans`),
        axios.get(`${API}/companies`)
      ]);
      setPlans(plansRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPlans = plans.filter(plan => {
    const matchesTab = activeTab === "all" || 
      (activeTab === "health" && plan.type !== "term" && plan.type !== "pa") ||
      (activeTab === "term" && plan.type === "term") ||
      (activeTab === "pa" && plan.type === "pa");
    
    const matchesSearch = searchQuery === "" || 
      plan.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plan.company?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCompany = selectedCompany === "all" || 
      plan.company_id === selectedCompany;
    
    return matchesTab && matchesSearch && matchesCompany;
  });

  const healthPlans = plans.filter(p => p.type !== "term" && p.type !== "pa");
  const termPlans = plans.filter(p => p.type === "term");
  const paPlans = plans.filter(p => p.type === "pa");

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8]">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-[#C9D4E6] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="text-[#2E3B57] hover:text-[#1B2A4A]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-bold text-[#1B2A4A] flex items-center gap-2">
              <Building2 className="h-6 w-6 text-[#2E3B57]" />
              All Plans
            </h1>
            <div className="w-20" />
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-8 px-4 sm:px-6 bg-gradient-to-b from-[#EDF1F8]/10 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1B2A4A] mb-2">
              Explore <span className="text-[#2E3B57]">{plans.length} Insurance Plans</span>
            </h1>
            <p className="text-[#3A4557] max-w-xl mx-auto">
              Compare features at a glance. Check what's included, what's add-on, and make an informed choice.
            </p>
          </div>

          {/* Stats & Legend */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-[#EDF1F8]/30 rounded-xl border border-[#C9D4E6]/50">
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30">
                <HeartPulse className="h-4 w-4 text-emerald-600" />
                <span className="text-emerald-600 font-medium text-sm">{healthPlans.length} Health</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EDF1F8]/40 border border-blue-500/30">
                <Shield className="h-4 w-4 text-[#2E3B57]" />
                <span className="text-[#2E3B57] font-medium text-sm">{termPlans.length} Term</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30" data-testid="pa-plans-count-badge">
                <Activity className="h-4 w-4 text-amber-600" />
                <span className="text-amber-600 font-medium text-sm">{paPlans.length} Accident</span>
              </div>
            </div>
            <Legend />
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="px-4 sm:px-6 pb-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 items-stretch lg:items-center justify-between">
            {/* Tab Selector */}
            <div className="grid grid-cols-4 lg:flex gap-1 sm:gap-1.5 p-1 bg-[#EDF1F8]/50 rounded-xl">
              <button
                onClick={() => setActiveTab("all")}
                className={`py-2 px-2 sm:px-4 rounded-lg font-medium text-xs sm:text-sm transition-all flex items-center justify-center ${
                  activeTab === "all"
                    ? "bg-[#D4A017] text-[#1B2A4A]"
                    : "text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#E2E9F4]"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveTab("health")}
                className={`py-2 px-2 sm:px-4 rounded-lg font-medium text-xs sm:text-sm transition-all flex items-center justify-center gap-1 sm:gap-1.5 ${
                  activeTab === "health"
                    ? "bg-emerald-500 text-white"
                    : "text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#E2E9F4]"
                }`}
              >
                <HeartPulse className="h-4 w-4 flex-shrink-0" />
                Health
              </button>
              <button
                onClick={() => setActiveTab("term")}
                className={`py-2 px-2 sm:px-4 rounded-lg font-medium text-xs sm:text-sm transition-all flex items-center justify-center gap-1 sm:gap-1.5 ${
                  activeTab === "term"
                    ? "bg-blue-500 text-white"
                    : "text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#E2E9F4]"
                }`}
              >
                <Shield className="h-4 w-4 flex-shrink-0" />
                Term
              </button>
              <button
                onClick={() => setActiveTab("pa")}
                className={`py-2 px-2 sm:px-4 rounded-lg font-medium text-xs sm:text-sm transition-all flex items-center justify-center gap-1 sm:gap-1.5 ${
                  activeTab === "pa"
                    ? "bg-amber-500 text-white"
                    : "text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#E2E9F4]"
                }`}
                data-testid="pa-filter-tab"
              >
                <Activity className="h-4 w-4 flex-shrink-0" />
                Accident
              </button>
            </div>

            {/* Search & Filter */}
            <div className="flex gap-2 sm:gap-3">
              <div className="relative flex-1 lg:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
                <input
                  type="text"
                  placeholder="Search plans..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full lg:w-56 pl-10 pr-4 py-2 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-lg text-[#1B2A4A] text-sm placeholder:text-[#3A4557] focus:outline-none focus:border-blue-500"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3A4557] hover:text-[#1B2A4A]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="flex-shrink-0 max-w-[42%] lg:max-w-none px-2 sm:px-3 py-2 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-lg text-[#1B2A4A] text-xs sm:text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="all">All Insurers</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Grid */}
      <section className="px-4 sm:px-6 pb-16">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredPlans.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#3A4557] text-lg">No plans found matching your criteria.</p>
              <Button 
                onClick={() => { setSearchQuery(""); setSelectedCompany("all"); setActiveTab("all"); }}
                className="mt-4"
                variant="outline"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {filteredPlans.map((plan) => (
                <PlanCard 
                  key={plan.id} 
                  plan={plan} 
                  onClick={() => navigate(`/plan/${plan.id}`)}
                />
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Feature Explanation */}
      <section className="px-4 sm:px-6 pb-12">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card p-6">
            <h3 className="text-[#1B2A4A] font-bold mb-4 flex items-center gap-2">
              <Info className="h-5 w-5 text-[#2E3B57]" />
              What do these features mean?
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              {HEALTH_CHECKLIST_FEATURES.map(feature => (
                <div key={feature.id} className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-lg bg-[#EDF1F8]/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[#1B2A4A] font-medium">{feature.label}</p>
                    <p className="text-[#3A4557] text-xs">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-[#C9D4E6]">
              <p className="text-amber-600 text-sm font-medium mb-3 flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Personal Accident features
              </p>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                {PA_CHECKLIST_FEATURES.map(feature => (
                  <div key={feature.id} className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg bg-[#EDF1F8]/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-[#1B2A4A] font-medium">{feature.label}</p>
                      <p className="text-[#3A4557] text-xs">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card p-6 text-center bg-gradient-to-r from-[#EDF1F8]/10 to-purple-500/10">
            <h3 className="text-xl font-bold text-[#1B2A4A] mb-2">Not sure which plan to choose?</h3>
            <p className="text-[#3A4557] mb-4 text-sm">
              Get personalized recommendations based on your health profile and budget.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                onClick={() => navigate("/insure-right")}
                className="bg-blue-500 hover:bg-[#EDF1F8] text-white"
              >
                Get Recommendations
              </Button>
              <Button
                onClick={() => navigate("/compare")}
                variant="outline"
                className="border-[#C9D4E6] text-[#2E3B57]"
              >
                Compare Plans
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white/80 border-t border-[#C9D4E6] py-6 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-[#3A4557] text-sm">
            © 2025 Kavach Box by Prashant K Uunadkat | Shree Finserv
          </p>
        </div>
      </footer>
    </div>
  );
}
